import { throttle, waitFor } from '@goodboydigital/astro';
import { ComponentInterface, Entity } from '@goodboydigital/odie';

import { Shared } from '../../App';
import { Settings } from '../../AppConfig';
import { CoinFactory } from '../entities/CoinEntity';
import { VehicleEntity } from '../entities/VehicleEntity';
import { Game } from '../Game';

export class PlayerComponent implements ComponentInterface
{
    public vehicle!: VehicleEntity;
    public axis = { x: 0, y: 0 };
    public canPlay = false;
    public soundId = 'player';

    private _prevAccelVal = 0;
    private _turboPressure = 0.4;
    private _pedalMod = 0;

    public reset(): void
    {
        this.axis.x = 0;
        this.axis.y = 0;
    }

    public addedToEntity(entity: Entity): void
    {
        if (!(entity instanceof VehicleEntity)) throw new Error('Player component only works with a VehicleEntity');
        this.vehicle = entity as VehicleEntity;
        this.vehicle.trick.onTrickDone.connect(this.onTrickDone.bind(this));
    }

    public update(): void
    {
        if (!this.vehicle || this.vehicle.crashed) return;

        const accel = this.vehicle.motor.acceleration = this.axis.x;
        const soundSystem = Shared.game.soundSystem;
        const wheelSpeed = this.vehicle.wheelLeft.physics.mainBody.angularVelocity;
        const wheelMagnitude = Math.sign(wheelSpeed) >= 0 ? wheelSpeed : wheelSpeed * -1;

        this.vehicle.motor.lean = this.axis.y;

        if (accel > 0 && this._pedalMod < 1) this._pedalMod += 0.1;
        if (accel < 1 && this._pedalMod > 0) this._pedalMod -= 0.1;

        // PLAYER SOUND
        soundSystem.updateBaseLoopTone(this.soundId, wheelMagnitude, this._pedalMod);

        if (this._turboPressure < 1 && accel !== 0) this._turboPressure += accel / 100;
        if (this._prevAccelVal < accel)
        {
            throttle(soundSystem.accelerating, 100, soundSystem)(this.soundId);
        }
        else if (accel === 0 && this._prevAccelVal > 0)
        {
            const magnitude = Math.sign(this._turboPressure >= 0 ? this._turboPressure : -this._turboPressure);

            throttle(soundSystem.release, 100, soundSystem)(this.soundId, magnitude);

            this._turboPressure = 0.4;
        }

        if (this._prevAccelVal >= 0 && accel === -1 && this.vehicle.wheelLeft.contacting)
        {
            throttle(soundSystem.braking, 100, soundSystem)(this.soundId);
        }

        this._prevAccelVal = accel;

        this.updateRacePerks();
    }

    private updateRacePerks(): void
    {
        const game = this.vehicle.scene as Game;

        this.vehicle.motor.maxSpeedMultiplier = game.race.racePerkMaxSpeed || 1;
        this.vehicle.motor.flipSpeedMultiplier = game.race.racePerkFlipSpeed || 1;
    }

    private onTrickDone(combo: number)
    {
        const game = this.vehicle.scene as Game;
        let numCoins = combo * Settings.trickCoins;

        if (game.race.racePerkTrickBonus) numCoins *= game.race.racePerkTrickBonus;

        this.vehicle.trick.coinsWidget.open();
        this.vehicle.trick.coinsWidget.label.text = `+${numCoins}`;

        this.vehicle.motor.boost();

        this.rewardCoins(numCoins);
    }

    private async rewardCoins(amount: number): Promise<void>
    {
        const game = this.vehicle.scene as Game;
        const step = (Math.PI * 2) / amount;
        let angle = 0;

        for (let i = 0; i < amount; i++)
        {
            angle += step;

            CoinFactory.create(game, {
                x: this.vehicle.character.physics.x + (Math.sin(angle) * 50),
                y: this.vehicle.character.physics.y + (Math.cos(angle) * 50),
                props: { bonus: true },
            });

            await waitFor(50);
        }
    }
}
